import React from 'react';

import { cssMap } from '@compiled/react';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import Button, { IconButton, SplitButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { browser } from '@atlaskit/editor-common/browser';
import type {
	Command,
	CommandDispatch,
	FloatingToolbarButton,
	FloatingToolbarCustom,
	Icon,
} from '@atlaskit/editor-common/types';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../../types/types';
import { AskRovoIcon } from '../../icons/prompt/askRovo';
import type { EditorPluginAIConfigItem } from '../../prebuilt/config-items/config-items';
import { improveWriting } from '../../prebuilt/config-items/prompts/enhance/enhance';

export const floatingToolbarAIButtonTestID = `ak-editor-ai-selection-toolbar-ai-button`;

const styles = cssMap({
	askAISplitButton: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'div:first-of-type': {
			outlineColor: 'transparent',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'div > div:nth-of-type(2)': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		button: {
			backgroundColor: 'transparent',
			display: 'flex',
			paddingTop: token('space.100'),
			paddingRight: token('space.100'),
			paddingBottom: token('space.100'),
			paddingLeft: token('space.100'),
			justifyContent: 'center',
			alignItems: 'center',
			gap: token('space.075', '6px'),
			alignSelf: 'stretch',
		},
	},
});

export const getAIExperienceButton = ({
	intl,
	rangeBaseGenerate,
	hideTitle,
	aiGlobalOptIn,
	icon,
	title,
	tooltip,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
}: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	hideTitle?: boolean;
	aiGlobalOptIn: AIGlobalOptIn;
	icon?: Icon;
	title?: MessageDescriptor;
	tooltip?: MessageDescriptor;
	triggeredFor?: NodeType;
	pulse?: boolean;
	onMount?: () => void;
	onClick?: () => void;
	disabled: boolean;
}) => {
	let didMouseEnter = false;

	const metaKey = browser.mac ? '⌘' : 'Ctrl';

	return {
		type: 'button',
		pulse,
		showTitle: true,
		testId: floatingToolbarAIButtonTestID,
		icon: icon ? icon : AtlassianIntelligenceIcon,
		...(!hideTitle && {
			title: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		}),
		tooltipContent: intl.formatMessage(tooltip ?? sharedMessages.aiUniversalShortCallToAction, {
			key: metaKey,
		}),
		onMount,
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: rangeBaseGenerate,
				lastTriggeredFrom: 'floatingToolbar',
				// ED-18194 Added analytics for floating toolbar to determine
				// the % of users who click the button vs shift tab to it
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				triggeredFor,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			if (onClick) {
				onClick();
			}

			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

export const getImproveWritingAIButton = ({
	intl,
	hideTitle,
	aiGlobalOptIn,
	disabled,
}: {
	intl: IntlShape;
	hideTitle?: boolean;
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
}) => {
	let didMouseEnter = false;

	return {
		type: 'button',
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-improve-writing`,
		icon:
			// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
			fg('ai-smart-button-team-25') || editorExperiment('platform_editor_controls', 'variant1')
				? MagicWandIcon
				: undefined,
		...(!hideTitle && {
			title: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		}),
		tooltipContent: intl.formatMessage(aiMessages.improveWritingToolbarIconTooltip),
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: improveWriting(),
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

export const getAskAIButton = ({
	intl,
	rangeBaseGenerate,
	aiGlobalOptIn,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
}: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	aiGlobalOptIn: AIGlobalOptIn;
	triggeredFor?: NodeType;
	pulse?: boolean;
	onMount?: () => void;
	onClick?: () => void;
	disabled: boolean;
}) => {
	const Icon = () => <RovoIcon size="small"></RovoIcon>;
	return getAIExperienceButton({
		intl,
		rangeBaseGenerate,
		hideTitle: false,
		aiGlobalOptIn,
		icon: Icon,
		title: aiMessages.askAIToolbarIconTitle,
		tooltip: editorExperiment('platform_editor_ai_quickstart_command', true)
			? aiMessages.askAIToolbarIconTooltipQuickCommand
			: aiMessages.askAIToolbarIconTooltip,
		triggeredFor,
		pulse,
		onMount,
		onClick,
		disabled,
	});
};

export const getAskRovoAiSplitButton = (props: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	aiGlobalOptIn: AIGlobalOptIn;
	triggeredFor?: NodeType;
	disabled: boolean;
}): FloatingToolbarCustom<Command> => {
	const { intl, rangeBaseGenerate, disabled, aiGlobalOptIn } = props;

	const metaKey = browser.mac ? '⌘' : 'Ctrl';

	const getPrimaryButton = (view: EditorView) => {
		const primaryButton = (
			<Button
				testId={floatingToolbarAIButtonTestID}
				isDisabled={disabled}
				iconBefore={AskRovoIcon}
				onClick={() => {
					const openAIModalCommand = createOpenAIModalCommand({
						state: view.state,
						configItem: rangeBaseGenerate,
						lastTriggeredFrom: 'floatingToolbarDropdown',
						// ED-18194 Added analytics for floating toolbar to determine
						// the % of users who click the button vs shift tab to it
						// triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
						aiGlobalOptIn,
						locale: intl.locale,
					});

					openAIModalCommand(view.state, view.dispatch);
				}}
			>
				{intl.formatMessage(aiMessages.askRovoAiSplitButtonLabel)}
			</Button>
		);

		return editorExperiment('platform_editor_ai_quickstart_command', true) ? (
			<Tooltip
				position="top"
				content={intl.formatMessage(aiMessages.askAIToolbarIconTooltipQuickCommand, {
					key: metaKey,
				})}
			>
				{primaryButton}
			</Tooltip>
		) : (
			primaryButton
		);
	};

	return {
		type: 'custom',
		fallback: [getAskAIButton(props)],
		render: (view) =>
			view && (
				<Inline xcss={styles.askAISplitButton}>
					<SplitButton>
						{getPrimaryButton(view)}
						<DropdownMenu<HTMLButtonElement>
							shouldRenderToParent
							trigger={({ triggerRef }) => (
								<IconButton
									isDisabled={disabled}
									ref={triggerRef}
									icon={() => <ChevronDownIcon label="Dropdown icon" size="small" />}
									label="Rovo quick actions dropdown menu trigger"
								/>
							)}
						></DropdownMenu>
					</SplitButton>
				</Inline>
			),
	};
};
